import React, { useState, useEffect } from 'react';
import { ArtifactService } from '../../../services/api/artifactService';
import { EnhancedArtifact } from '../../../types/artifacts';

export interface ArtifactSelectorProps {
  value?: string;
  onChange: (artifactId: string) => void;
  placeholder?: string;
  disabled?: boolean;
  excludeArtifactId?: string; // Exclude this artifact from the list
  style?: React.CSSProperties;
  showDescription?: boolean;
}

export const ArtifactSelector: React.FC<ArtifactSelectorProps> = ({
  value,
  onChange,
  placeholder = 'Select parent artifact',
  disabled = false,
  excludeArtifactId,
  style = {},
  showDescription = false
}) => {
  const [artifacts, setArtifacts] = useState<EnhancedArtifact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadArtifacts();
  }, [excludeArtifactId]);

  const loadArtifacts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let potentialArtifacts: EnhancedArtifact[];
      
      if (excludeArtifactId) {
        potentialArtifacts = await ArtifactService.getPotentialParents(excludeArtifactId);
      } else {
        potentialArtifacts = await ArtifactService.getArtifacts();
      }
      
      setArtifacts(potentialArtifacts);
    } catch (err) {
      console.error('Failed to load artifacts:', err);
      setError('Failed to load artifacts');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(event.target.value);
  };

  if (loading) {
    return (
      <select
        disabled
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #d1d5db',
          borderRadius: '6px',
          fontSize: '14px',
          backgroundColor: '#f9fafb',
          color: '#6b7280',
          ...style
        }}
      >
        <option value="">Loading artifacts...</option>
      </select>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '8px 12px',
        border: '1px solid #fca5a5',
        borderRadius: '6px',
        backgroundColor: '#fef2f2',
        color: '#dc2626',
        fontSize: '14px',
        ...style
      }}>
        {error}
      </div>
    );
  }

  return (
    <select
      value={value || ''}
      onChange={handleChange}
      disabled={disabled}
      style={{
        width: '100%',
        padding: '8px 12px',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        fontSize: '14px',
        backgroundColor: disabled ? '#f9fafb' : 'white',
        color: disabled ? '#6b7280' : '#1f2937',
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...style
      }}
    >
      <option value="">{placeholder}</option>
      {artifacts.map((artifact) => (
        <option key={artifact.id} value={artifact.id}>
          {showDescription && artifact.description 
            ? `${artifact.name} - ${artifact.description.substring(0, 50)}${artifact.description.length > 50 ? '...' : ''}`
            : artifact.name
          }
        </option>
      ))}
    </select>
  );
};
